import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const buttonStyles: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-contrast hover:bg-accent-hover',
  secondary: 'bg-surface-raised text-text border border-border-subtle hover:border-border-strong',
  ghost: 'text-text-muted hover:text-text hover:bg-surface-raised',
  danger: 'bg-danger-subtle text-danger hover:brightness-95',
};

export function Button({
  variant = 'secondary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm
        font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50
        ${buttonStyles[variant]} ${className}`}
      {...props}
    />
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  children: (id: string, describedBy: string | undefined) => ReactNode;
}

/**
 * Labelled form field. The label is always a real `<label>` bound to the control, and hints and
 * errors are wired through aria-describedby rather than left as decoration.
 */
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-text">
        {label}
      </label>

      {children(id, describedBy)}

      {hint && !error && (
        <p id={hintId} className="text-xs text-text-muted">
          {hint}
        </p>
      )}

      {error && (
        <p id={errorId} className="text-xs text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function TextInput({
  invalid = false,
  className = '',
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return (
    <input
      aria-invalid={invalid || undefined}
      className={`w-full rounded-md border bg-surface px-3 py-2 text-sm text-text
        placeholder:text-text-faint
        ${invalid ? 'border-danger' : 'border-border-subtle'} ${className}`}
      {...props}
    />
  );
}

export function Banner({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'error' | 'success';
  children: ReactNode;
}) {
  const tones = {
    info: 'bg-surface-raised text-text-muted border-border-subtle',
    error: 'bg-danger-subtle text-danger border-danger',
    success: 'bg-surface-raised text-success border-border-subtle',
  } as const;

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`rounded-md border px-3 py-2 text-sm ${tones[tone]}`}
    >
      {children}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" className="flex items-center gap-2 text-sm text-text-muted">
      <span
        aria-hidden="true"
        className="size-3 animate-spin rounded-full border-2 border-border-strong border-t-accent"
      />
      {label}
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
      <p className="text-sm font-medium text-text">{title}</p>
      {children && <p className="max-w-sm text-sm text-text-muted">{children}</p>}
    </div>
  );
}
